// WhatsApp short-link resolver (link.ourteam.ai/s/<code>) — ticket #2935.
// BE: ourteam-backend#3393, router app/interface/routers/short_links.py.
//
// NO auth: candidates are a no-login flow, so the request carries no
// Authorization header and must work with zero session (incognito).
//
// SECURITY: the resolved `original_url` embeds the candidate's auth token —
// callers must NEVER render, log, or persist it; the only permitted use is an
// immediate `window.location.replace(...)`.
import axios from "axios";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

// Discriminated result so the page can branch without touching raw errors:
//   ok        → redirect immediately (a successful resolve counts a click)
//   not_found → unknown code (404)
//   expired   → 410, `message` is the API's user-facing copy
//   error     → network / 5xx — manual retry only
export type ShortLinkResolveResult =
    | { kind: "ok"; originalUrl: string }
    | { kind: "not_found" }
    | { kind: "expired"; message: string }
    | { kind: "error" };

export async function resolveShortLink(
    shortCode: string
): Promise<ShortLinkResolveResult> {
    try {
        // validateStatus: accept everything — the contract branches on the
        // status (the body `code` field mirrors it), not on axios throwing.
        const res = await axios.get(
            `${apiURL}/s/${encodeURIComponent(shortCode)}/resolve`,
            { validateStatus: () => true, timeout: 15000 }
        );
        const body = (res.data ?? {}) as {
            code?: number;
            message?: string;
            data?: { original_url?: string };
        };
        const status = body.code ?? res.status;
        if (res.status === 200 && body.data?.original_url) {
            return { kind: "ok", originalUrl: body.data.original_url };
        }
        if (status === 404) return { kind: "not_found" };
        if (status === 410) {
            return {
                kind: "expired",
                message:
                    body.message ||
                    "This link has expired. Please request a new one.",
            };
        }
        return { kind: "error" };
    } catch {
        // Network failure (no response) — do NOT log the error object either;
        // axios errors can echo the request config/URL.
        return { kind: "error" };
    }
}

/**
 * Resolve a WhatsApp AD short link (link.ourteam.ai/wa/<code>) — #3269,
 * BE ourteam-backend#4012. Mirrors resolveShortLink under the /wa namespace:
 * marketing links, stable per job, never expire (the 410 branch is kept only
 * as graceful handling). A successful resolve counts a click — call it ONCE.
 */
export async function resolveWaShortLink(
    shortCode: string
): Promise<ShortLinkResolveResult> {
    try {
        const res = await axios.get(
            `${apiURL}/wa/${encodeURIComponent(shortCode)}/resolve`,
            { validateStatus: () => true, timeout: 15000 }
        );
        const body = (res.data ?? {}) as {
            code?: number;
            message?: string;
            data?: { original_url?: string };
        };
        const status = body.code ?? res.status;
        if (res.status === 200 && body.data?.original_url) {
            return { kind: "ok", originalUrl: body.data.original_url };
        }
        if (status === 404) return { kind: "not_found" };
        if (status === 410) {
            return { kind: "expired", message: body.message || "This link has expired." };
        }
        return { kind: "error" };
    } catch {
        return { kind: "error" };
    }
}
